import { ShieldAlert } from "lucide-react";
import { heldActionsFor, HELD_TREATMENT } from "../livingRoom.js";
import { reasonLabel } from "./ModerationRow";

// LR19 — **THE YELLOW. ONE COMPONENT, BOTH SURFACES, AND IT DRAWS NOTHING FOR ANYBODY BUT HER.**
//
// Her ruling: *"on OWNER accounts ONLY, a held comment (and held post) renders with a clear YELLOW
// treatment in the Living Room itself, unmistakable at a glance. Tapping the yellow item right there
// offers her seat's moderation actions inline."*
//
// **IT TAKES A TREATMENT RATHER THAN AN ITEM AND A VIEWER, AND THAT IS THE DESIGN.** The decision of
// *whether there is anything to draw* is `heldTreatment` in `src/livingRoom.js` — a shared law a gate
// RUNS over every combination of viewer and visibility. If this component took the item and the
// viewer it would be a second place that decides who sees a hold, which is the one thing this room
// may never have two of (the store's own opening law, paid for three times in these files). Here the
// answer arrives already made, and `treatment === null` draws NOTHING — no wrapper, no empty box,
// no class on the row.
//
// **HOOKLESS, so `scripts/lr19-held-in-room.mjs` CALLS it** and reads the tree back: the yellow for
// a held comment, no yellow for a public one, both controls behind the tap, and — the check that
// matters most — nothing at all when the law says nothing.

export type HeldTreatment = { state: string; notice: string; reason: string | null; canApprove: boolean };
export type HeldAction = { key: string; label: string; verb: string; note: string };

export function HeldStrip({
  treatment, open, busy, onToggle, onAct,
}: {
  treatment: HeldTreatment | null;
  open: boolean;
  busy: string;
  onToggle: () => void;
  onAct: (action: string) => void;
}) {
  if (!treatment) return null;
  const actions = heldActionsFor(treatment) as HeldAction[];
  return (
    <div className={`lr-held lr-held--${treatment.state}`} data-held={treatment.state}>
      {/* THE BAR IS THE TAP TARGET, so "tapping the yellow item" is the yellow itself rather than a
          separate control she has to find. `aria-expanded` because it opens something, and the
          reason is INSIDE the button: the sentence and the rule that produced it are one fact. */}
      <button
        type="button" className="lr-held__bar" onClick={onToggle} aria-expanded={open}
        aria-label={treatment.notice}
      >
        <ShieldAlert size={14} className="lr-held__icon" />
        <span className="lr-held__notice">{treatment.notice}</span>
        {treatment.reason ? <span className="lr-held__reason">{reasonLabel(treatment.reason)}</span> : null}
      </button>
      {/* A REMOVED row offers no actions and says so by having none — `heldActionsFor` returns an
          empty list, and T9-2's law applies to this box as much as to a report section: no body, no
          box. A disabled Approve on something already gone would be a control that lies twice. */}
      {open && actions.length ? (
        <div className="lr-held__acts">
          {actions.map((a) => (
            <button
              key={a.key} type="button" title={a.note}
              className={`lr-held__act lr-held__act--${a.key}`}
              disabled={Boolean(busy)}
              onClick={() => onAct(a.key)}
            >
              {busy === a.key ? "…" : a.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// The two states, re-exported so a caller naming one is naming the law's own constant rather than a
// string it typed (R24: a literal at a call site is held by nothing at all).
export const HELD_STATES = HELD_TREATMENT;
